import { useState, useEffect } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { X, Clock, Check, Mail } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export default function CreateCampaignModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [vessels, setVessels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [importedFiles, setImportedFiles] = useState([]);
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [selectedVessels, setSelectedVessels] = useState([]);
  const [recipientType, setRecipientType] = useState("vessels");
  
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    content: "",
    template_id: "",
    imported_file_id: "",
    email_config_id: "",
    scheduleType: "now",
    scheduledDate: "",
    scheduledTime: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vesselsRes, templatesRes, filesRes, configsRes] = await Promise.all([
        api.get("/vessels?active_only=true"),
        api.get("/templates"),
        api.get("/vessels/imports"),
        api.get("/email-configs")
      ]);
      setVessels(vesselsRes.data);
      setTemplates(templatesRes.data);
      setImportedFiles(filesRes.data.filter(f => f.status === "imported"));
      setEmailConfigs(configsRes.data);
      
      // Set default email config
      const defaultConfig = configsRes.data.find(c => c.is_default);
      if (defaultConfig) {
        setFormData(prev => ({ ...prev, email_config_id: defaultConfig.config_id }));
      }
    } catch (error) {
      console.error("Failed to load data");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTemplateSelect = (templateId) => {
    const actualId = templateId === "none" ? "" : templateId;
    setFormData(prev => ({ ...prev, template_id: actualId }));
    
    if (actualId) {
      const template = templates.find(t => t.template_id === actualId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          subject: template.subject,
          content: template.content
        }));
      }
    }
  };

  const handleVesselToggle = (vesselId) => {
    setSelectedVessels(prev => 
      prev.includes(vesselId)
        ? prev.filter(id => id !== vesselId)
        : [...prev, vesselId]
    );
  };

  const handleSelectAll = () => {
    setSelectedVessels(vessels.map(v => v.vessel_id));
  };

  const handleSelectNone = () => {
    setSelectedVessels([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subject || !formData.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (recipientType === "vessels" && selectedVessels.length === 0) {
      toast.error("Please select at least one vessel");
      return;
    }

    if (recipientType === "file" && !formData.imported_file_id) {
      toast.error("Please select an imported file");
      return;
    }
    
    if (!formData.email_config_id) {
      toast.error("Please select an email configuration to send from");
      return;
    }

    setLoading(true);

    try {
      let scheduled_at = null;
      if (formData.scheduleType === "scheduled" && formData.scheduledDate && formData.scheduledTime) {
        scheduled_at = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).toISOString();
      }

      const data = {
        name: formData.name,
        subject: formData.subject,
        content: formData.content,
        template_id: formData.template_id || null,
        email_config_id: formData.email_config_id,
        scheduled_at,
        timezone: formData.timezone,
        ...(recipientType === "vessels" 
          ? { vessel_ids: selectedVessels }
          : { imported_file_id: formData.imported_file_id }
        )
      };

      await api.post("/campaigns", data);
      toast.success("Campaign created successfully");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Create Campaign
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Campaign Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Q4 Newsletter"
                  data-testid="campaign-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template">Template (Optional)</Label>
                <Select value={formData.template_id} onValueChange={handleTemplateSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.template_id} value={template.template_id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Send From Email Selection */}
            <div className="space-y-2">
              <Label htmlFor="email_config" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Send From *
              </Label>
              <Select 
                value={formData.email_config_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, email_config_id: value }))}
              >
                <SelectTrigger data-testid="email-config-select">
                  <SelectValue placeholder="Select email to send from" />
                </SelectTrigger>
                <SelectContent>
                  {emailConfigs.length === 0 ? (
                    <SelectItem value="none" disabled>No email configs - Add in Settings</SelectItem>
                  ) : (
                    emailConfigs.map((config) => (
                      <SelectItem key={config.config_id} value={config.config_id}>
                        <div className="flex items-center gap-2">
                          <span>{config.from_email}</span>
                          {config.is_default && (
                            <span className="text-xs text-primary">(Default)</span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {emailConfigs.length === 0 && (
                <p className="text-xs text-amber-500">
                  No email configurations found. Please add one in Settings first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject Line *</Label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="Important updates for {{name}}"
                data-testid="campaign-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Email Content *</Label>
              <Textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows={8}
                placeholder="Dear {{contact_person}},&#10;&#10;We hope this message finds you well..."
                className="font-mono text-sm"
                data-testid="campaign-content-input"
              />
              <p className="text-xs text-muted-foreground">
                Available variables: {"{{name}}"}, {"{{company}}"}, {"{{contact_person}}"}, {"{{email}}"}
              </p>
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Schedule
            </h3>
            
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleType"
                  value="now"
                  checked={formData.scheduleType === "now"}
                  onChange={handleChange}
                  className="text-primary"
                />
                <span>Send immediately</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleType"
                  value="scheduled"
                  checked={formData.scheduleType === "scheduled"}
                  onChange={handleChange}
                  className="text-primary"
                />
                <Clock className="h-4 w-4" />
                <span>Schedule for later</span>
              </label>
            </div>

            {formData.scheduleType === "scheduled" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-secondary/30 rounded-lg">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    name="scheduledDate"
                    value={formData.scheduledDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Input
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>

          {/* Recipients */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Recipients
            </h3>
            
            <div className="flex gap-4 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="vessels"
                  checked={recipientType === "vessels"}
                  onChange={(e) => setRecipientType(e.target.value)}
                  className="text-primary"
                />
                <span>Select vessels</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="file"
                  checked={recipientType === "file"}
                  onChange={(e) => setRecipientType(e.target.value)}
                  className="text-primary"
                />
                <span>Use imported file</span>
              </label>
            </div>

            {recipientType === "vessels" ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {selectedVessels.length} of {vessels.length} selected
                  </span>
                  <div className="flex gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={handleSelectAll}>
                      Select All
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={handleSelectNone}>
                      Select None
                    </Button>
                  </div>
                </div>
                
                <div className="max-h-48 overflow-y-auto border border-border rounded-lg">
                  {vessels.map((vessel) => (
                    <div
                      key={vessel.vessel_id}
                      className="flex items-center gap-3 p-3 hover:bg-secondary/30 border-b border-border last:border-0 cursor-pointer"
                      onClick={() => handleVesselToggle(vessel.vessel_id)}
                    >
                      <div className={`h-4 w-4 shrink-0 rounded-sm border border-primary shadow flex items-center justify-center ${selectedVessels.includes(vessel.vessel_id) ? 'bg-primary text-primary-foreground' : ''}`}>
                        {selectedVessels.includes(vessel.vessel_id) && <Check className="h-3 w-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{vessel.name}</div>
                        <div className="text-sm text-muted-foreground truncate">{vessel.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Select Imported File</Label>
                <Select 
                  value={formData.imported_file_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, imported_file_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a file" />
                  </SelectTrigger>
                  <SelectContent>
                    {importedFiles.map((file) => (
                      <SelectItem key={file.file_id} value={file.file_id}>
                        {file.file_name} ({file.total_emails} emails)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="btn-primary" 
              disabled={loading}
              data-testid="create-campaign-submit"
            >
              {loading ? "Creating..." : formData.scheduleType === "scheduled" ? "Schedule Campaign" : "Create Campaign"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
